import {Component, computed, EventEmitter, HostListener, inject, Input, Output} from '@angular/core';
import {DecimalPipe} from '@angular/common';
import * as XLSX from 'xlsx';
import {DomSanitizer} from "@angular/platform-browser";
import {AppIcons} from "@/assets/icons/icons";
import {ThemeService} from '@/app/core/services/theme/theme.service';


@Component({
  selector: 'app-bulk-upload-modal',
  standalone: true,
  imports: [DecimalPipe],
  templateUrl: './bulk-upload-modal.component.html',
  styleUrls: ['./bulk-upload-modal.component.scss']
})
export class BulkUploadModalComponent {

  @Input() resourceLabel = 'Ocupaciones';
  @Input() maxFileSizeMb = 5;
  @Input() accept = '.xlsx,.csv';
  /** Eventos */
  @Output() closeModal = new EventEmitter<void>();
  @Output() confirm = new EventEmitter<File>();
  @Output() downloadFormat = new EventEmitter<string>();
  // estado local
  file?: File;
  errorMsg = '';
  isDragging = false;
  protected step = 1;
  private readonly themeService = inject(ThemeService);
  protected readonly AppIcons = AppIcons;
  protected themeContent = this.themeService.themeContent;
  protected bulkUploadModalContet = computed(() => this.themeContent()?.shared.bulkUploadModal);

  constructor(protected sanitizer: DomSanitizer) {
  }

  onFileSelected(ev: Event) {
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];
    this.setFile(file);
  }

  onDrop(ev: DragEvent) {
    ev.preventDefault();
    this.isDragging = false;
    const file = ev.dataTransfer?.files?.[0];
    this.setFile(file);
  }

  onDragOver(ev: DragEvent) {
    ev.preventDefault();
    this.isDragging = true;
  }

  onDragLeave() {
    this.isDragging = false;
  }

  removeFile() {
    this.file = undefined;
    this.errorMsg = '';
  }

  handleCancel() {
    this.removeFile();
    this.closeModal.emit();
  }

  handleConfirm() {
    if (!this.file) return;
    this.confirm.emit(this.file);
  }

  descargarPlantilla(): void {
    // 1) Datos: título + fila en blanco + encabezados
    const data: (string | null)[][] = [
      ['Carga de datos', null],
      [null, null],
      ['nombre']
    ];

    // 2) Hoja con merges y anchos de columna
    const ws = XLSX.utils.aoa_to_sheet(data);

    // Merge de A1:B1 para el título
    ws['!merges'] = [{s: {r: 0, c: 0}, e: {r: 0, c: 1}}];

    // Anchos de columnas (opcionales, solo para que se vea mejor)
    ws['!cols'] = [{wch: 28}, {wch: 50}];

    // 3) Libro y escritura
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Formato');

    // Nombre del archivo según el recurso
    const base = this.resourceLabel?.toLowerCase() === 'departamentos' ? 'departamentos' : 'ocupaciones';
    const fileName = `formato_${base}.xlsx`;

    XLSX.writeFile(wb, fileName);
  }

  onBack(): void {
    if (this.step === 2) {
      this.step = 1;
      return;
    }
    this.handleCancel();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.onBack();
  }

  private setFile(file?: File) {
    this.errorMsg = '';
    this.file = undefined;

    if (!file) return;

    // Validaciones mínimas de UI
    const okType = this.accept
      .split(',')
      .map(s => s.trim().toLowerCase())
      .some(ext => file.name.toLowerCase().endsWith(ext));

    if (!okType) {
      this.errorMsg = `Formato no soportado. Usa ${this.accept}`;
      return;
    }

    const maxBytes = this.maxFileSizeMb * 1024 * 1024;
    if (file.size > maxBytes) {
      this.errorMsg = `El archivo excede ${this.maxFileSizeMb} MB.`;
      return;
    }

    this.file = file;
  }

}
